import { db, auth } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import {
  getVehicleScore,
  getFleetHealth,
  calculateNextMaintenance,
  getRulesForVehicle,
} from '../utils/maintenanceRules';
import { snapshotMigrationConfig } from '../utils/migrationConfig';
import { EMERGENCIAS_ACTIVAS } from '../utils/featureFlags';
import { VEHICLE_TYPES } from '../utils/vehicleData';
import { createSupportTicket as apiCreateSupportTicket } from '../api/listings';
import {
  createFuelLog as apiCreateFuelLog,
  createVehicle as apiCreateVehicle,
  vehicleApiBody,
} from '../api/vehicles';

/**
 * ¿El expediente del carro ya vive en Postgres?
 *
 * Se pregunta DENTRO de cada acción y no una vez al montar: el asistente es un
 * objeto global que sobrevive a la navegación, así que un valor tomado al abrir
 * el chat podría seguir vigente horas después de una reversión. Ante cualquier
 * duda, el camino viejo.
 */
async function enPostgres() {
  try {
    return (await snapshotMigrationConfig()).readsFrom('vehicles') === 'postgres';
  } catch {
    return false;
  }
}

/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║ 🔴 EL VOCABULARIO DE CARROCERÍAS ES UNO SOLO, Y NO LO ELIGE EL ASISTENTE. ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 * La herramienta `registrarVehiculo` le ofrecía al modelo una lista inventada
 * (`sedan`, `hatchback`, `coupe`, `sport`, `wagon`, `micro`, `4wd`) que no existe
 * en ninguna parte del sistema. El único vocabulario real es `VEHICLE_TYPES`, que
 * es exactamente el enum `VehicleType` de Postgres — y como `VehicleCreateSchema`
 * es `.strict()`, un `vehicleType: 'SEDAN'` no se ignora: devuelve 400 y NO GUARDA
 * NADA. O sea que el alta más común del país (un sedán) no se podía hacer.
 *
 * Por eso la lista se DERIVA de `VEHICLE_TYPES` en vez de copiarse: una copia más
 * de estas once palabras es justo lo que produjo el fallo.
 */
export const TIPOS_VEHICULO = VEHICLE_TYPES.map(x => x.value);

/**
 * Lo que la gente DICE, traducido a lo que la base entiende. Solo entran las
 * equivalencias que no admiten discusión; «guagua» (¿minibús o autobús?) y «4x4»
 * (que es una tracción, no una carrocería) se quedan fuera a propósito.
 */
const SINONIMOS_TIPO = {
  sedan: 'auto', coche: 'auto', carro: 'auto', automovil: 'auto', turismo: 'auto',
  hatchback: 'auto', coupe: 'auto', wagon: 'auto', deportivo: 'auto', sport: 'auto',
  jeepeta: 'suv', yipeta: 'suv', jeep: 'suv', crossover: 'suv',
  camioneta: 'pickup', pick_up: 'pickup',
  furgoneta: 'van', minivan: 'van',
  motocicleta: 'moto', pasola: 'moto', scooter: 'moto',
  microbus: 'minibus', minibus_pequeno: 'minibus',
  autobus: 'bus',
  camion_pesado: 'camion_pes', patana: 'camion_pes', trailer: 'camion_pes',
};

/**
 * El tipo que dictó el modelo, o `null` si no se entiende.
 *
 * `null` significa «no lo sé», y quien no sabe NO afirma: quien llama omite el
 * campo y la base aplica su valor por defecto. La alternativa —mandar lo que
 * sea— es la que tumbaba el registro entero por una palabra secundaria: el dueño
 * pidió guardar su carro, no clasificarlo.
 */
export function normalizeVehicleType(raw) {
  const s = String(raw ?? '')
    .trim()
    .toLowerCase()
    // NFD separa el acento de la letra y el filtro siguiente lo descarta:
    // «sedán» → «sedan». Se filtra por lo PERMITIDO (letras, cifras, espacio y
    // guion) en vez de listar signos a quitar, que es una lista que siempre
    // acaba incompleta.
    .normalize('NFD').replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s-]+/g, '_');
  if (!s) return null;
  if (TIPOS_VEHICULO.includes(s)) return s;
  return SINONIMOS_TIPO[s] || null;
}

/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║ 🔴 UNA QUEJA DICHA AL ASISTENTE SE PERDÍA.                                ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 * El asistente escuchaba el reclamo, respondía con comprensión y no lo archivaba
 * en ninguna parte: el dueño de MekaFix nunca se enteraba. `enviarComentario`
 * abre un CASO DE SOPORTE igual que el formulario de Ayuda, en la MISMA
 * colección, para que caiga en la bandeja que ya se mira (`/admin/support`) y el
 * cliente lo pueda seguir en "Mis casos". Una colección nueva sería una bandeja
 * nueva, y una bandeja nueva es una bandeja que nadie abre.
 */
const COMENTARIO_MAX = 2000;   // el API admite 5000; el asunto, 200
const RESUMEN_MAX = 100;       // lo que cabe en el asunto sin taparlo

/**
 * Queja o sugerencia, a partir de lo que dicte el modelo.
 *
 * Ante la duda, QUEJA: un reclamo archivado como idea se lee como algo que se
 * puede ver la semana que viene, y esa es la pérdida cara. Al revés solo cuesta
 * una etiqueta.
 */
export function normalizarTipoComentario(raw) {
  return /sugerenc|suggest|idea/i.test(String(raw ?? '')) ? 'sugerencia' : 'queja';
}

/** La etiqueta del tipo, para hablarle al usuario. */
function etiquetaComentario(clase, t) {
  return t(clase === 'sugerencia' ? 'assistant.comentario.tipoSugerencia' : 'assistant.comentario.tipoQueja');
}

/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║ EL PUENTE CON LOS CASOS QUE SE ABRIERON ANTES DEL CAMPO `kind`.           ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 * Esos casos llevan el tipo SOLO dentro del asunto, y el asunto lo compone i18n:
 * hay una versión en español y otra en inglés, según el idioma que tuviera el
 * cliente al hablar. Clasificar solo por `kind` los mandaría al cajón de
 * "Ayuda" — y desaparecer de un filtro es peor que no tener filtro, porque el
 * dueño leería "no hay quejas viejas".
 *
 * Los prefijos se declaran aquí, en el mismo archivo que COMPONE el asunto, y una
 * prueba centinela comprueba que sigan siendo los que `assistant.comentario.
 * subject*` escribe de verdad en los dos locales. Si alguien retoca ese texto, se
 * pone rojo en vez de dejar de clasificar en silencio.
 */
export const PREFIJOS_ASUNTO_COMENTARIO = {
  queja: ['Queja desde el asistente', 'Complaint from the assistant'],
  sugerencia: ['Sugerencia desde el asistente', 'Suggestion from the assistant'],
};

/**
 * De qué es un caso de soporte: 'queja', 'sugerencia', o `null` si lo abrió una
 * persona por el Centro de ayuda (que no es ni lo uno ni lo otro).
 */
export function claseDeComentario(ticket) {
  const explicito = String(ticket?.kind ?? '').trim().toLowerCase();
  if (explicito === 'queja' || explicito === 'sugerencia') return explicito;

  const asunto = String(ticket?.subject ?? '').trim();
  if (!asunto) return null;
  for (const clase of Object.keys(PREFIJOS_ASUNTO_COMENTARIO)) {
    if (PREFIJOS_ASUNTO_COMENTARIO[clase].some(p => asunto.startsWith(p))) return clase;
  }
  return null;
}

/**
 * Abre el caso de soporte. Devuelve el tipo normalizado; LANZA si no se escribió
 * (quien llama NO puede afirmar que quedó enviado sin que esto haya resuelto).
 *
 * ⚠️ `type: 'general'` es obligado, no una elección: el API acepta solo
 * GENERAL/TECHNICAL/ORDER/APPOINTMENT, y los dos últimos exigen una referencia.
 * Inventar un tipo "QUEJA" sería un 400. Lo que distingue el caso es el ASUNTO,
 * que es además lo único que el dueño lee sin abrirlo.
 */
export async function crearComentarioDeSoporte({ tipo, texto, profile }, t) {
  const cuerpo = String(texto ?? '').trim().slice(0, COMENTARIO_MAX);
  if (!cuerpo) throw new Error('comentario vacío');
  if (!auth.currentUser) throw new Error('sin sesión');
  const clase = normalizarTipoComentario(tipo);
  const resumen = cuerpo.replace(/\s+/g, ' ').slice(0, RESUMEN_MAX);
  const subject = t(
    clase === 'sugerencia' ? 'assistant.comentario.subjectSugerencia' : 'assistant.comentario.subjectQueja',
    { resumen },
  );

  let porApi = false;
  try {
    porApi = (await snapshotMigrationConfig()).readsFrom('listings') === 'postgres';
  } catch (_) { /* ante cualquier duda, el camino viejo */ }

  if (porApi) {
    // El nombre, el correo y el estado los pone el SERVIDOR; mandarlos sería un
    // 400 que los nombra. `source` tampoco viaja: esa columna no existe.
    await apiCreateSupportTicket({ type: 'general', subject, description: cuerpo });
    return clase;
  }

  const p = profile || {};
  await addDoc(collection(db, 'supportTickets'), {
    userId: auth.currentUser.uid,
    userEmail: p.email || auth.currentUser.email || '',
    userName: p.fullName || auth.currentUser.displayName || '',
    type: 'general',
    subject,
    description: cuerpo,
    referenceId: null,
    referenceLabel: null,
    referenceOwnerId: null,
    referenceOwnerName: null,
    status: 'open',
    // Lo permiten las reglas (el create solo prohíbe los campos de SANCIÓN) y le
    // dice al dueño de dónde salió el caso sin tener que deducirlo del asunto.
    source: 'assistant',
    // 🔴 EL TIPO, EN SU PROPIO CAMPO Y NO SOLO DENTRO DEL ASUNTO. Es lo que deja
    // separar quejas de sugerencias en la bandeja sin leer texto libre. Las
    // reglas lo admiten (el create de supportTickets solo prohíbe los cuatro
    // campos de SANCIÓN).
    //
    // ⚠️ Por el camino del API NO viaja: esa columna no existe y mandarla sería
    // un 400. Los casos que entren por ahí se clasifican por el asunto, igual que
    // los de antes de este campo (`claseDeComentario`).
    kind: clase,
    // 🔴 Marca del SERVIDOR, no `new Date()`: la bandeja del admin ordena por
    // `createdAt.toMillis()`, y una fecha sin ese método vale 0, o sea que el
    // caso nace en el FONDO de la lista. Llegar al último lugar es casi no
    // llegar.
    createdAt: serverTimestamp(),
  });
  return clase;
}

// Mapa de tipo de cada herramienta. DEBE mantenerse en sincronía con
// ASSISTANT_TOOLS en functions/index.js (las functions no importan src/).
//   read     → se resuelve con datos ya cargados y se responde en el chat.
//   navigate → lleva a una pantalla pre-llenada (confirma antes).
//   write    → escribe en Firestore tras una tarjeta de confirmación.
export const TOOL_KINDS = {
  consultarSalud: 'read',
  proximoMantenimiento: 'read',
  verCitas: 'navigate',
  agendarCita: 'navigate',
  // Emergencias oculta (EMERGENCIAS_ACTIVAS=false): sin esta entrada el asistente no ejecuta
  // solicitarAsistencia. El lado servidor (ASSISTANT_TOOLS en functions/src/ai.js) tiene su
  // propio interruptor EMERGENCIAS_ACTIVAS: encender AMBOS para restaurarla.
  ...(EMERGENCIAS_ACTIVAS ? { solicitarAsistencia: 'navigate' } : {}),
  buscarRepuesto: 'navigate',
  registrarCombustible: 'write',
  registrarVehiculo: 'write',
  // Aquí vivía `enviarSugerencia`, que escribía en la colección `suggestions`.
  // Se retiró del asistente porque una idea podía caer en uno de DOS buzones
  // según con qué palabras hablara el cliente, y el segundo buzón es siempre el
  // que nadie abre. `enviarComentario` cubre los dos tipos. La página pública
  // /sugerencias y su bandeja /admin/sugerencias siguen en pie: tienen su propio
  // alimentador y su propio aviso a los admins.
  enviarComentario: 'write',
  irA: 'navigate',
  diagnosticar: 'diagnose',
};

const DEST_ROUTES = {
  vehicles: '/vehicles',
  appointments: '/appointments',
  marketplace: '/marketplace',
  orders: '/my-orders',
  mechanics: '/mechanics',
  profile: '/dashboard',
  home: '/home',
  ...(EMERGENCIAS_ACTIVAS ? { asistencia: '/asistencia' } : {}),
};

export function kindOf(name) {
  return TOOL_KINDS[name] || null;
}

// Acciones que exigen un vehículo resuelto sí o sí (si hay ambigüedad, se pregunta).
export function needsVehicle(name) {
  return name === 'registrarCombustible' || name === 'agendarCita' || name === 'diagnosticar';
}

function resolveVehicle(ctx, vehiculoId) {
  if (vehiculoId) return ctx.vehicles.find(v => v.id === vehiculoId) || null;
  if (ctx.vehicles.length === 1) return ctx.vehicles[0];
  return null;
}

// true si la acción requiere un vehículo y no se pudo resolver (ambiguo) → preguntar.
export function vehicleMissing(name, args, ctx) {
  return needsVehicle(name) && !resolveVehicle(ctx, args.vehiculoId);
}

function vehicleName(v) {
  return v ? `${v.brand} ${v.model}${v.year ? ' ' + v.year : ''}` : '';
}

// Próximo servicio (vencido primero, luego el más cercano por km).
function nextService(vehicle, records) {
  const rules = getRulesForVehicle(vehicle);
  const latestByType = {};
  (records || []).forEach(r => {
    const cur = latestByType[r.serviceType];
    if (!cur || (r.serviceDate?.toDate?.() > cur.serviceDate?.toDate?.())) latestByType[r.serviceType] = r;
  });
  const results = Object.values(latestByType)
    .map(r => calculateNextMaintenance(r, vehicle.currentMileage, rules))
    .filter(Boolean);
  if (!results.length) return null;
  results.sort((a, b) => {
    if (a.status === 'overdue' && b.status !== 'overdue') return -1;
    if (a.status !== 'overdue' && b.status === 'overdue') return 1;
    return (a.kmRemaining ?? Infinity) - (b.kmRemaining ?? Infinity);
  });
  return results[0];
}

// ── Lectura: devuelve el texto a mostrar en el chat ──────────────────────────
export function runRead(name, args, ctx, t) {
  if (!ctx.vehicles.length) return t('assistant.read.noVehicles');

  if (name === 'consultarSalud') {
    const v = resolveVehicle(ctx, args.vehiculoId);
    if (!v) {
      // Sin vehículo concreto → salud de la flota.
      const fleet = getFleetHealth(ctx.vehicles.map(x => ctx.alertsByVehicle[x.id] || []));
      return t('assistant.read.fleetHealth', { score: fleet });
    }
    const alerts = ctx.alertsByVehicle[v.id] || [];
    const score = getVehicleScore(alerts);
    const alertsText = alerts.length ? alerts.map(a => a.message).join('. ') : t('assistant.read.noAlerts');
    return t('assistant.read.vehicleHealth', { vehiculo: vehicleName(v), score, alerts: alertsText });
  }

  if (name === 'proximoMantenimiento') {
    const v = resolveVehicle(ctx, args.vehiculoId);
    if (!v) return t('assistant.ask.whichVehicle', { list: ctx.vehicles.map(vehicleName).join(', ') });
    const next = nextService(v, ctx.recordsByVehicle[v.id]);
    if (!next) return t('assistant.read.noRecords', { vehiculo: vehicleName(v) });
    return t('assistant.read.nextMaintenance', { vehiculo: vehicleName(v), message: next.message });
  }

  return '';
}

// ── Texto de la tarjeta de confirmación (navigate / write) ───────────────────
export function describeAction(name, args, ctx, t) {
  switch (name) {
    case 'verCitas':
      return t('assistant.confirm.verCitas');
    case 'agendarCita': {
      const v = resolveVehicle(ctx, args.vehiculoId);
      const veh = vehicleName(v) || t('assistant.yourVehicle');
      return args.servicio
        ? t('assistant.confirm.agendarCita', { servicio: args.servicio, vehiculo: veh })
        : t('assistant.confirm.agendarCitaNoService', { vehiculo: veh });
    }
    case 'solicitarAsistencia':
      return t('assistant.confirm.solicitarAsistencia', { tipo: t(`emergency.svc.${args.tipo}.label`) });
    case 'buscarRepuesto': {
      const v = resolveVehicle(ctx, args.vehiculoId);
      const forVehicle = v ? t('assistant.confirm.forVehicle', { vehiculo: vehicleName(v) }) : '';
      return t('assistant.confirm.buscarRepuesto', { texto: args.texto, forVehicle });
    }
    case 'registrarCombustible': {
      const v = resolveVehicle(ctx, args.vehiculoId);
      const g = Number(args.galones);
      const eff = g > 0 ? Number(args.kmRecorridos) / g : 0;
      return t('assistant.confirm.registrarCombustible', {
        galones: args.galones,
        km: Number(args.kmRecorridos).toLocaleString(),
        vehiculo: vehicleName(v) || t('assistant.yourVehicle'),
        eff: eff.toFixed(1),
      });
    }
    case 'registrarVehiculo':
      return t('assistant.confirm.registrarVehiculo', { vehiculo: `${args.marca || ''} ${args.modelo || ''}${args.ano ? ' ' + args.ano : ''}`.trim() });
    case 'enviarComentario': {
      // El usuario tiene que LEER lo que se va a mandar a su nombre antes de
      // confirmarlo: un reclamo mal resumido por el modelo se convierte en una
      // queja que él nunca puso.
      const clase = normalizarTipoComentario(args.tipo);
      return t('assistant.confirm.enviarComentario', {
        tipo: etiquetaComentario(clase, t),
        texto: String(args.texto ?? '').trim(),
      });
    }
    case 'irA':
      return t('assistant.confirm.irA', { destino: t(`assistant.dest.${args.destino}`) });
    default:
      return '';
  }
}

// ── Ejecuta la acción (navigate / write). Devuelve texto de confirmación o ''. ─
export async function executeAction(name, args, ctx, t) {
  const { navigate } = ctx;

  switch (name) {
    case 'verCitas':
      navigate('/appointments');
      return '';

    case 'agendarCita': {
      const v = resolveVehicle(ctx, args.vehiculoId);
      sessionStorage.setItem('appointmentContext', JSON.stringify({
        vehicleId: v?.id || '',
        issue: args.servicio || '',
      }));
      navigate('/mechanics');
      return '';
    }

    case 'solicitarAsistencia':
      navigate('/emergency-request', { state: { serviceType: args.tipo } });
      return '';

    case 'buscarRepuesto': {
      const v = resolveVehicle(ctx, args.vehiculoId);
      navigate('/marketplace', { state: { search: args.texto, vehicleId: v?.id || null } });
      return '';
    }

    case 'irA':
      navigate(DEST_ROUTES[args.destino] || '/home');
      return '';

    case 'registrarCombustible': {
      const v = resolveVehicle(ctx, args.vehiculoId);
      if (!v) return t('assistant.read.noVehicles');
      const kmDriven = Number(args.kmRecorridos);
      const gallons = Number(args.galones);
      const efficiency = gallons > 0 ? kmDriven / gallons : 0;
      if (await enPostgres()) {
        // El rendimiento lo calcula el SERVIDOR; `efficiency` solo se usa para
        // contestarle al usuario. Marca, modelo y año no viajan: viven en el
        // carro, y copiarlos en cada tanqueo es lo que hace que un día dejen de
        // coincidir con la ficha.
        await apiCreateFuelLog(v.id, { kmDriven, gallons, condition: 'MIXED' });
      } else {
        await addDoc(collection(db, 'fuelLogs'), {
          vehicleId: v.id,
          userId: auth.currentUser.uid,
          brand: v.brand,
          model: v.model,
          year: v.year,
          kmDriven,
          gallons,
          efficiency,
          condition: 'mixed',
          createdAt: new Date(),
        });
      }
      return t('assistant.done.registrarCombustible', { eff: efficiency.toFixed(1) });
    }

    case 'registrarVehiculo': {
      if (!args.marca || !args.modelo) return t('assistant.registrarVehiculoMissing');
      // 🔴 Guardarlo en la base que ya no se lee sería lo peor que puede hacer
      // esta herramienta: el asistente confirma «registré tu carro» y el dueño
      // no lo ve nunca en su garaje.
      // 🔴 El tipo se TRADUCE, y si no se entiende se OMITE. Mandarlo tal cual
      // es lo que devolvía 400 y dejaba al dueño sin su carro (ver el recuadro
      // de `normalizeVehicleType`).
      const tipo = normalizeVehicleType(args.tipo);
      const nuevoCarro = {
        brand: args.marca,
        model: args.modelo,
        year: args.ano ? Number(args.ano) : null,
        currentMileage: args.km ? Number(args.km) : 0,
        ...(tipo ? { vehicleType: tipo } : {}),
      };
      if (await enPostgres()) {
        // `userId` y las fechas NO viajan: el dueño de un carro no se elige y las
        // fechas las pone la base. El esquema es `.strict()` y las nombra en el
        // 400, así que colarlas no guardaría a medias — no guardaría nada.
        await apiCreateVehicle(vehicleApiBody(nuevoCarro, { create: true }));
      } else {
        await addDoc(collection(db, 'vehicles'), {
          userId: auth.currentUser.uid,
          ...nuevoCarro,
          createdAt: new Date(),
          updatedAt: new Date(),
        });
      }
      return t('assistant.done.registrarVehiculo', { vehiculo: `${args.marca} ${args.modelo}`.trim() });
    }

    case 'enviarComentario': {
      if (!String(args.texto ?? '').trim()) return t('assistant.enviarComentarioMissing');
      // Si esto lanza, el panel enseña "no se pudo y no se guardó nada". Es lo
      // correcto: la confirmación de abajo solo se devuelve si el caso existe.
      const clase = await crearComentarioDeSoporte(
        { tipo: args.tipo, texto: args.texto, profile: ctx.profile }, t,
      );
      return t('assistant.done.enviarComentario', { tipo: etiquetaComentario(clase, t) });
    }

    default:
      return '';
  }
}
